import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { loginUser, checkAuth, loginVerify2fa } from "../utils/api";
import { AuthState } from "./authType";
const SESSION_DURATION = 12 * 3600000;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoggedOut: true,
      error: "",
      loading: false,
      loadingPage: false,
      intervalId: null,
      curr: "RUB",

      changeCurrency: (curr: string) => set({ curr }),

      /** Превращает detail бэкенда в текст: он бывает строкой и объектом. */
      describeAuthError: (err: any) => {
        if (!err.response) {
          return err.request
            ? "Ошибка подключения к серверу."
            : "Произошла ошибка при отправке запроса.";
        }
        const detail = err.response.data?.detail;
        if (detail && typeof detail === "object") {
          // Блокировка после серии неверных кодов приходит с locked_until.
          if (detail.locked_until) {
            const until = new Date(detail.locked_until).toLocaleTimeString(
              "ru-RU",
              { hour: "2-digit", minute: "2-digit" },
            );
            return `Слишком много неверных кодов. Повторите после ${until}`;
          }
          return detail.message || "Неизвестная ошибка";
        }
        return `Ошибка: ${err.response.status} - ${detail || "Неизвестная ошибка"}`;
      },

      /** Открывает сессию после успешного входа — общая часть обоих шагов. */
      completeLogin: (user: string | null) => {
        localStorage.setItem(
          "auth_expiration",
          String(Date.now() + SESSION_DURATION),
        );
        get().checkAuthStatus();
        get().startSessionCheck();
        set({ isAuthenticated: true, user, isLoggedOut: false });
      },

      login: async (username, password) => {
        set({ loading: true, error: "" });

        try {
          const response = await loginUser({ username, password });

          // Пароль верный, но доступа ещё нет: cookie access_token не выдана,
          // вместо неё поставлена короткоживущая cookie на второй шаг.
          if (response.data?.twofa_required) {
            return { ok: false, twofaRequired: true };
          }

          get().completeLogin(response.data.user);
          return { ok: true, twofaRequired: false };
        } catch (err: any) {
          set({ error: get().describeAuthError(err) });
          return { ok: false, twofaRequired: false };
        } finally {
          set({ loading: false });
        }
      },

      verifyTwoFactor: async (code: string) => {
        set({ loading: true, error: "" });
        try {
          const response = await loginVerify2fa(code);
          get().completeLogin(response.data?.user ?? null);
          return { ok: true };
        } catch (err: any) {
          set({ error: get().describeAuthError(err) });
          return { ok: false };
        } finally {
          set({ loading: false });
        }
      },

      logout: () => {
        clearTimeout(get().intervalId!);
        localStorage.removeItem("auth_expiration");

        set({
          isAuthenticated: false,
          user: null,
          isLoggedOut: true,
          intervalId: null,
          loadingPage: false,
        });
      },

      checkAuthStatus: () => {
        const expirationTime = Number(localStorage.getItem("auth_expiration"));
        if (Date.now() > expirationTime) {
          get().logout();
          return;
        }

        set({ loadingPage: true });
        checkAuth()
          .then((response) => {
            if (!response.data.user || response.data.user.role === "merchant") {
              get().logout();
              return;
            }
            set({
              isAuthenticated: true,
              user: response.data.user,
              isLoggedOut: false,
            });
          })
          .catch(() => {
            get().logout();
          })
          .finally(() => set({ loadingPage: false }));
      },

      startSessionCheck: () => {
        if (get().intervalId) return;

        const expirationTime = Number(localStorage.getItem("auth_expiration"));
        const timeLeft = expirationTime - Date.now();

        if (timeLeft <= 0) {
          get().logout();
          return;
        }

        const timeoutId = setTimeout(() => {
          get().logout();
        }, timeLeft);

        set({ intervalId: timeoutId });
      },
    }),
    {
      name: "auth-store",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        isLoggedOut: state.isLoggedOut,
        curr: state.curr,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.checkAuthStatus();
        }
      },
    },
  ),
);
